import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { isEmpty, loadRawScript, copyToClipboard, deep_copy } from '@/util';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { FroalaTextarea } from '@/components/Froala/FroalaTextarea';
import { WidgetHeader } from './WidgetHeader';
import { useTranslation } from 'react-i18next';
import { encode } from '@/base64';
import { initMath, MathView } from './MathView';
import {
  InputSelect,
  InputText,
  InputTextarea,
  Modal,
  Button,
} from '@teo/components';
import { Question } from '@teo/components/icons';
import { ErrorBox } from '@/components/ErrorBox';
import './MathEdit.css';

export const initialMath = () => ({
  noSubmit: true,
  type: 'Math',
  latex: '',
});

export const initialInlineMath = () => ({
  noSubmit: true,
  type: 'Math',
  latex: '',
  inline: true,
});

export const MathEdit = ({
  state,
  index,
  onModified = undefined,
  setStateFn = undefined,
}) => {
  const { t } = useTranslation();
  const [latex, setLatex] = useState(state.latex ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState(state.title ?? '');

  // Persisted refs
  const editorRef = useRef(null); // MQ container
  const previewRef = useRef(null); // KaTeX preview node
  const mqRef = useRef(null); // MQ instance

  // Guards for sync & caret
  const suppressMQHandlerRef = useRef(false); // ignore MQ edit handler during programmatic .latex()
  const changeOriginRef = useRef(null); // 'mq' | 'advanced' | null
  const advSelRef = useRef(null); // { el, start, end }

  // Provide getState only on changes (prevents parent churn)
  useEffect(() => {
    if (!setStateFn) return;
    const getState = async () => {
      const newState = deep_copy(state);
      newState.latex = latex;
      newState.title = title;
      return newState;
    };
    setStateFn(getState);
  }, [latex, title, setStateFn, state]);

  // KaTeX preview (unchanged behavior)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await initMath();
      if (cancelled) return;
      try {
        if (window.katex && previewRef.current) {
          window.katex.render(latex || '', previewRef.current, {
            displayMode: true,
          });
        }
        setError(null);
      } catch (e) {
        setError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [latex, isOpen]);

  // Attach MathQuill once, keep it alive
  const attachMQ = useCallback(async (node) => {
    editorRef.current = node;
    if (!node) return;
    await initMath();
    if (!mqRef.current) {
      const MQ = window.MathQuill.getInterface(2);
      mqRef.current = MQ.MathField(node, {
        handlers: {
          edit: () => {
            // user typing in SIMPLE editor
            if (suppressMQHandlerRef.current) return; // ignore programmatic sets
            changeOriginRef.current = 'mq';
            setLatex(mqRef.current.latex());
          },
        },
      });
      if (latex) mqRef.current.latex(latex);
    }
  }, []); // stable

  // When advanced editor changes, push into MQ without firing its handler
  useEffect(() => {
    if (!mqRef.current) return;

    if (changeOriginRef.current === 'mq') {
      // Change came from MQ (simple editor), don't push back.
      changeOriginRef.current = null;
      return;
    }

    // Change came from ADVANCED editor or external
    const current = mqRef.current.latex();
    if (current !== latex) {
      suppressMQHandlerRef.current = true;
      mqRef.current.latex(latex);
      // Release the suppression on next microtask/paint so MQ won't echo back
      Promise.resolve().then(() => {
        suppressMQHandlerRef.current = false;
      });
    }
  }, [latex]);

  // Restore caret in advanced textarea when typing there
  useLayoutEffect(() => {
    if (changeOriginRef.current !== 'advanced') return;
    changeOriginRef.current = null;

    const sel = advSelRef.current;
    if (!sel || !sel.el) return;

    // Best effort: restore the selection if element is still in DOM
    try {
      if (document.contains(sel.el)) {
        sel.el.setSelectionRange(sel.start, sel.end);
      }
    } catch (_) {
      // ignore if component doesn't expose the native textarea or selection API
    }
  }, [latex]);

  return (
    <>
      <Modal isOpen={isOpen}>
        <Modal.Header onClose={() => setIsOpen(false)}>
          {t(
            !state.inline
              ? 'widgets.math.title_block'
              : 'widgets.math.title_inline'
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4 lg:p-4">
            {!state.inline && (
              <>
                <span className="text-lg font-semibold text-grey-04">
                  {t('widgets.math.formula_title')}
                </span>
                <InputText
                  onChange={(ev) => setTitle(ev.target.value)}
                  value={title}
                />
              </>
            )}

            <span className="text-lg font-semibold text-grey-04">
              {t('widgets.math.simple_editor')}
            </span>
            <div
              ref={attachMQ}
              className="textarea _katex"
              style={{
                boxShadow: 'none',
                display: 'block',
                height: '100px',
                borderRadius: '6px',
                borderWidth: '1px',
                backgroundColor: 'rgba(30, 30, 32, 0.05)',
              }}
              id={state.uid + '_mq_editcontent'}
            />

            <span className="text-lg font-semibold text-grey-04">
              {t('widgets.math.advanced_editor')}
              <a
                href="https://katex.org/docs/supported.html"
                target="_blank"
                className=""
                title="Supported Functions"
                rel="noreferrer"
              >
                <Question className="ml-1 inline-block h-6 w-6 text-secondary-04" />
              </a>
            </span>

            <InputTextarea
              id="outlined-multiline-static"
              label={t('Script')}
              value={latex}
              onChange={(e) => {
                // Remember caret and mark origin before state update
                const el = e.target;
                try {
                  advSelRef.current = {
                    el,
                    start: el.selectionStart,
                    end: el.selectionEnd,
                  };
                } catch (_) {
                  advSelRef.current = { el: null, start: 0, end: 0 };
                }
                changeOriginRef.current = 'advanced';
                setLatex(e.target.value);
              }}
              multiline
              rows={4}
            />

            <span className="text-lg font-semibold text-grey-04">
              {t('widgets.math.preview')}
            </span>
            {error && <ErrorBox text={error.message} />}

            <div
              className="_katex"
              style={{ padding: 20, justifyContent: 'center' }}
            >
              <span style={{ minHeight: '20px' }} ref={previewRef}></span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full justify-between">
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              {t('forms.cancel')}
            </Button>
            <Button onClick={() => setIsOpen(false)}>
              {t('forms.confirm_formal')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <MathView state={{ ...state, latex }} onClick={() => setIsOpen(true)} />
    </>
  );
};

MathEdit.syncStates = (state, answer, correction) => {}; // nothing to sync; kept for compatibility
